/**
 * Pure helper to compute canonical Atlassian browse URLs for Jira issues.
 */

export interface JiraBrowseUrlOptions {
  baseUrl?: string;
  siteName?: string;
  rawSelfUrl?: string;
}

function cleanBaseUrl(url: string): string {
  const trimmed = url.trim().replace(/\/+$/, '');
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

export function buildJiraBrowseUrl(
  ticketKey?: string,
  options?: JiraBrowseUrlOptions,
): string {
  if (!ticketKey || !ticketKey.trim()) {
    return '';
  }

  const key = ticketKey.trim();

  // 1. Preferred: configured user settings baseUrl
  if (options?.baseUrl && options.baseUrl.trim()) {
    const cleaned = cleanBaseUrl(options.baseUrl);
    if (cleaned) {
      return `${cleaned}/browse/${encodeURIComponent(key)}`;
    }
  }

  // 2. OAuth site name if available (e.g. "myteam" or "myteam.atlassian.net")
  if (options?.siteName && options.siteName.trim()) {
    const site = options.siteName.trim().replace(/\.atlassian\.net$/i, '');
    if (site) {
      return `https://${encodeURIComponent(site)}.atlassian.net/browse/${encodeURIComponent(key)}`;
    }
  }

  // 3. Extract origin from raw Jira issue self URL, skipping API gateway origins (api.atlassian.com)
  if (options?.rawSelfUrl && options.rawSelfUrl.trim()) {
    try {
      const parsed = new URL(options.rawSelfUrl.trim());
      if (
        parsed.origin &&
        parsed.origin !== 'null' &&
        parsed.hostname !== 'api.atlassian.com'
      ) {
        return `${parsed.origin}/browse/${encodeURIComponent(key)}`;
      }
    } catch {
      // invalid URL format, ignore
    }
  }

  // 4. Default fallback
  return `https://atlassian.net/browse/${encodeURIComponent(key)}`;
}
